/**
 * CTRL_12 Dashboard - Purpose Limitation
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const PURPOSE_LABELS = {
  'training': { label: 'Model Training', color: '#1890ff' },
  'inference': { label: 'Model Inference', color: '#52c41a' },
  'testing': { label: 'Testing/Validation', color: '#722ed1' },
  'research': { label: 'Research & Development', color: '#fa8c16' },
  'analytics': { label: 'Analytics', color: '#13c2c2' },
  'improvement': { label: 'Service Improvement', color: '#eb2f96' }
};

function CTRL_12_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_12';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate purpose coverage
  const purposeCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, documented: 0, percentage: 0 };

    const documented = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.data_purpose || metadata.purpose_documented === true;
    }).length;

    return {
      total: events.length,
      documented,
      percentage: Math.round((documented / events.length) * 100)
    };
  }, [events]);

  // Calculate purpose distribution
  const purposeDistribution = useMemo(() => {
    const distribution = {};

    events.forEach(e => {
      const purpose = e.event_metadata?.data_purpose;
      if (purpose) {
        distribution[purpose] = (distribution[purpose] || 0) + 1;
      }
    });

    return Object.entries(distribution).map(([purpose, count]) => ({
      purpose,
      label: PURPOSE_LABELS[purpose]?.label || purpose,
      color: PURPOSE_LABELS[purpose]?.color || '#888',
      count,
      percentage: events.length > 0 ? Math.round((count / events.length) * 100) : 0
    }));
  }, [events]);

  // Calculate consent tracking
  const consentTracking = useMemo(() => {
    const withConsent = events.filter(e => e.event_metadata?.user_consent === true).length;

    const mechanismCounts = {};
    events.forEach(e => {
      const mechanism = e.event_metadata?.consent_mechanism;
      if (mechanism) {
        mechanismCounts[mechanism] = (mechanismCounts[mechanism] || 0) + 1;
      }
    });

    return {
      total: events.length,
      withConsent,
      percentage: events.length > 0 ? Math.round((withConsent / events.length) * 100) : 0,
      mechanisms: Object.entries(mechanismCounts).map(([mechanism, count]) => ({ mechanism, count }))
    };
  }, [events]);

  // Find violations (events without purpose or consent)
  const violations = useMemo(() => {
    return events.filter(e => {
      const metadata = e.event_metadata || {};
      return !metadata.data_purpose || metadata.user_consent !== true;
    });
  }, [events]);

  // Table columns for violations
  const violationColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true
    },
    {
      title: 'Purpose',
      key: 'purpose',
      render: (_, record) => {
        const purpose = record.event_metadata?.data_purpose;
        return purpose ?
          <Tag color={PURPOSE_LABELS[purpose]?.color || '#888'}>{PURPOSE_LABELS[purpose]?.label || purpose}</Tag> :
          <Tag color="red">Missing</Tag>;
      }
    },
    {
      title: 'Consent',
      key: 'consent',
      align: 'center',
      render: (_, record) => {
        const hasConsent = record.event_metadata?.user_consent === true;
        return hasConsent ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<WarningOutlined />}>Missing</Tag>;
      }
    },
    {
      title: 'Consent Mechanism',
      dataIndex: ['event_metadata', 'consent_mechanism'],
      key: 'mechanism',
      render: (mechanism) => mechanism || '-'
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_12: Purpose Limitation Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Purpose Coverage"
              value={purposeCoverage.percentage}
              suffix="%"
              prefix={purposeCoverage.percentage >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: purposeCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {purposeCoverage.documented} of {purposeCoverage.total} events
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Consent Rate"
              value={consentTracking.percentage}
              suffix="%"
              prefix={<SafetyOutlined />}
              valueStyle={{ color: consentTracking.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {consentTracking.withConsent} of {consentTracking.total} with consent
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Violations"
              value={violations.length}
              prefix={violations.length === 0 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: violations.length === 0 ? '#52c41a' : '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Purpose Types"
              value={purposeDistribution.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Purpose Distribution */}
      <Card title="Purpose Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {purposeDistribution.map((item, idx) => (
            <Col xs={24} sm={12} md={8} key={idx} style={{ marginBottom: '16px' }}>
              <Card size="small" style={{ backgroundColor: `${item.color}15`, borderColor: item.color }}>
                <Statistic
                  title={item.label}
                  value={item.count}
                  suffix={`(${item.percentage}%)`}
                  valueStyle={{ color: item.color }}
                />
                <Progress
                  percent={item.percentage}
                  strokeColor={item.color}
                  size="small"
                  style={{ marginTop: '8px' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Consent Mechanism Breakdown */}
      {consentTracking.mechanisms.length > 0 && (
        <Card title="Consent Mechanisms" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            {consentTracking.mechanisms.map((item, idx) => (
              <Col xs={24} sm={12} key={idx} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 'bold' }}>{item.mechanism}</span>
                  <span>{item.count} events</span>
                </div>
                <Progress
                  percent={Math.round((item.count / consentTracking.total) * 100)}
                  size="small"
                  strokeColor="#52c41a"
                />
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Violations Table */}
      <Card title={`Violations (${violations.length})`}>
        <Table
          dataSource={violations.slice(0, 20)}
          columns={violationColumns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} violations`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_12_Dashboard;
